'use client';

import { ArrowLeft, ArrowRight, ChevronLeft, RotateCcw } from 'lucide-react';
import Link from 'next/link';

import { cn } from '@/shared/utils';

export type ChatNavigation = {
	backHref: string;
	backLabel: string;
	progress?: { current: number; total: number };
};

type Props = {
	navigation?: ChatNavigation;
	onTitleClick?: () => void;
	titleActionLabel?: string;
	onRestart?: () => void;
	restartDisabled?: boolean;
	title: string;
	previousHref?: string;
	nextHref?: string;
	onPrevious?: () => void;
	onNext?: () => void;
	onNavigate?: (action: 'back' | 'previous' | 'next') => void;
	candidate?: boolean;
	readOnly?: boolean;
	timer?: { text: string; low: boolean; totalTime?: string };
};

const arrowStyle =
	'flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-3 transition-colors hover:bg-gray-4/30 hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-3';

export function ChatTopbar({
	navigation,
	title,
	onTitleClick,
	titleActionLabel,
	onRestart,
	restartDisabled,
	previousHref,
	nextHref,
	onPrevious,
	onNext,
	onNavigate,
	candidate,
	readOnly,
	timer,
}: Props) {
	const progress = navigation?.progress;
	return (
		<header className="shrink-0 bg-gray-5 px-3 sm:px-6">
			{navigation && !candidate && <h1 className="sr-only">{title}</h1>}
			<nav
				aria-label={
					navigation
						? candidate
							? 'Навигация по собеседованию'
							: 'Навигация по материалам'
						: 'Управление диалогом'
				}
				className="mx-auto flex h-14 max-w-[720px] items-center justify-between gap-2 border-b border-gray-4/50"
			>
				{navigation && !candidate ? (
					<Link
						href={navigation.backHref}
						onClick={() => onNavigate?.('back')}
						className="flex h-9 min-w-0 items-center gap-1 rounded-lg pr-2 text-sm text-gray-3 hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
					>
						<ChevronLeft size={16} className="shrink-0" aria-hidden="true" />
						<span className="truncate">{navigation.backLabel}</span>
					</Link>
				) : (
					<h1
						aria-label={title}
						title={title}
						className="min-w-0 flex-1 truncate text-sm font-medium text-gray-3"
					>
						{onTitleClick ? (
							<button
								type="button"
								onClick={onTitleClick}
								aria-label={titleActionLabel}
								title={title}
								className="block h-9 max-w-full cursor-pointer truncate rounded-lg text-left transition-colors hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
							>
								{title}
							</button>
						) : (
							title
						)}
					</h1>
				)}
				<div className="flex shrink-0 items-center gap-1">
					{navigation && !candidate && !readOnly && (
						<button
							type="button"
							className={arrowStyle}
							disabled={!previousHref}
							aria-label="Предыдущий материал"
							title="Предыдущий материал"
							onClick={() => {
								onNavigate?.('previous');
								onPrevious?.();
							}}
						>
							<ArrowLeft size={16} />
						</button>
					)}
					{progress && !candidate && (
						<span
							className="min-w-12 text-center text-sm tabular-nums text-gray-3"
							aria-label={`Материал ${progress.current} из ${progress.total}`}
						>
							{progress.current} / {progress.total}
						</span>
					)}
					{navigation && !candidate && !readOnly && (
						<button
							type="button"
							className={arrowStyle}
							disabled={!nextHref}
							aria-label="Следующий материал"
							title="Следующий материал"
							onClick={() => {
								onNavigate?.('next');
								onNext?.();
							}}
						>
							<ArrowRight size={16} />
						</button>
					)}
					{timer && (
						<span
							className={cn(
								'ml-1 rounded-lg px-2 py-1.5 font-mono text-sm tabular-nums',
								timer.low ? 'text-red-hover' : 'text-gray-3',
								timer.low && !candidate && 'bg-red-light',
							)}
							aria-label={`Оставшееся время текущей задачи ${timer.text}${timer.totalTime ? `. Общее время: ${timer.totalTime}` : ''}`}
							title={`Оставшееся время задачи${timer.totalTime ? `. Интервью: ${timer.totalTime}` : ''}`}
						>
							{timer.text}
						</span>
					)}
					{onRestart && (
						<button
							type="button"
							className={arrowStyle}
							onClick={onRestart}
							disabled={restartDisabled}
							aria-label="Начать заново"
							title="Начать заново"
						>
							<RotateCcw size={16} aria-hidden="true" />
						</button>
					)}
					{readOnly && <span className="text-xs text-gray-3">Просмотр</span>}
				</div>
			</nav>
		</header>
	);
}
